import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { CHAIN_LABELS, STRATEGY_LABELS, formatNum } from '../utils/masterChefUtils';

const TYPE_OPTIONS = ['', 'recovery', 'pump', 'volume', 'ema'];
const CHAIN_OPTIONS = ['', 'solana', 'base', 'ethereum', 'bsc'];

function pnlClass(v) {
  if (v > 0.0001) return 'text-green-600 dark:text-green-400';
  if (v < -0.0001) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-400';
}

function StatusBadge({ enabled }) {
  return enabled ? (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Active</span>
  ) : (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Paused</span>
  );
}

function ExecutionModeBadge({ isLive }) {
  return isLive ? (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Live</span>
  ) : (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Dry run</span>
  );
}

export default function UserStrategies() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [enabled, setEnabled] = useState('');
  const [type, setType] = useState('');
  const [chain, setChain] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (enabled) params.set('enabled', enabled);
      if (type) params.set('type', type);
      if (chain) params.set('chain', chain);
      const { data } = await api.get(`/user-strategies?${params}`);
      setStrategies(data.strategies || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load strategies');
    } finally {
      setLoading(false);
    }
  }, [search, enabled, type, chain]);

  useEffect(() => { load(); }, [load]);

  const totals = strategies.reduce(
    (acc, s) => ({
      open: acc.open + (s.openPositions || 0),
      live: acc.live + (s.enabled ? 1 : 0),
      pnl: acc.pnl + (s.totalPnl || 0),
    }),
    { open: 0, live: 0, pnl: 0 }
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Strategies</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          All Master Chef strategies deployed by premium users — status, PnL, and ownership.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total strategies</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{strategies.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Active</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{totals.live}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Open positions</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{totals.open}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Combined PnL</div>
          <div className={`text-2xl font-bold mt-1 ${pnlClass(totals.pnl)}`}>
            {totals.pnl >= 0 ? '+' : ''}{formatNum(totals.pnl, 4)} native
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Strategy, user, telegram ID…"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select value={enabled} onChange={(e) => setEnabled(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Paused</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
            <option value="">All types</option>
            {TYPE_OPTIONS.filter(Boolean).map((t) => (
              <option key={t} value={t}>{STRATEGY_LABELS[t] || t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Chain</label>
          <select value={chain} onChange={(e) => setChain(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm">
            <option value="">All chains</option>
            {CHAIN_OPTIONS.filter(Boolean).map((c) => (
              <option key={c} value={c}>{CHAIN_LABELS[c] || c}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={load} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          Refresh
        </button>
      </div>

      {error && <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading strategies…</div>
        ) : strategies.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No user strategies found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Strategy</th>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Chain</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3 text-right">Open</th>
                  <th className="px-4 py-3 text-right">Closed</th>
                  <th className="px-4 py-3 text-right">Win rate</th>
                  <th className="px-4 py-3 text-right">Unrealized</th>
                  <th className="px-4 py-3 text-right">Realized</th>
                  <th className="px-4 py-3 text-right">Total PnL</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {strategies.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{s.name}</div>
                      <div className="text-[10px] text-gray-400 font-mono truncate max-w-[140px]" title={s.id}>{s.id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 dark:text-white">
                        {s.ownerUsername ? `@${s.ownerUsername}` : s.ownerFirstName || '—'}
                      </div>
                      <div className="text-[10px] text-gray-400 font-mono">{s.ownerTelegramId}</div>
                      {s.ownerTier && (
                        <span className="text-[10px] uppercase text-blue-600 dark:text-blue-400">{s.ownerTier}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 capitalize">{STRATEGY_LABELS[s.type] || s.type}</td>
                    <td className="px-4 py-3 capitalize">{CHAIN_LABELS[s.chain] || s.chain}</td>
                    <td className="px-4 py-3"><StatusBadge enabled={s.enabled} /></td>
                    <td className="px-4 py-3"><ExecutionModeBadge isLive={s.isLive !== false} /></td>
                    <td className="px-4 py-3 text-right font-mono">{s.openPositions}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.closedPositions}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.closedPositions ? `${s.winRate}%` : '—'}</td>
                    <td className={`px-4 py-3 text-right font-mono ${pnlClass(s.unrealizedPnl)}`}>{formatNum(s.unrealizedPnl, 4)}</td>
                    <td className={`px-4 py-3 text-right font-mono ${pnlClass(s.realizedPnl)}`}>{formatNum(s.realizedPnl, 4)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${pnlClass(s.totalPnl)}`}>
                      {s.totalPnl >= 0 ? '+' : ''}{formatNum(s.totalPnl, 4)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
